import { LDateTime } from '@app/components/date/LDateTime';
import { advertiserFieldMessage } from '@app/modules/audit/modules/station-audit/components/AdvertiserField';
import { campaignFieldMessage } from '@app/modules/audit/modules/station-audit/components/CampaignField';
import { orderFieldMessage } from '@app/modules/audit/modules/station-audit/components/OrderField';
import {
  diffLabelMessage,
  toleranceHelpMessage,
  toleranceLabelMessage,
} from '@app/modules/audit/modules/station-audit/components/schedule/schedule-match-details.messages';
import { ScheduleOffToleranceWarning } from '@app/modules/audit/modules/station-audit/components/schedule/ScheduleOffToleranceWarning';
import { ScheduleTolerance } from '@app/modules/audit/modules/station-audit/components/schedule/ScheduleTolerance';
import { versionFieldMessage } from '@app/modules/audit/modules/station-audit/components/VersionField';
import { AuditSchedule } from '@mi-front/core';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Box, Divider, Stack, Tooltip, Typography } from '@mui/material';
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { SchedulesListCategory } from './build-schedules-list-order-filter-url';
import { ConfirmedByScheduleOrderFilterButton } from './ConfirmedByScheduleOrderFilterButton';

const scheduleDatetimeLabelMessage = defineMessage({
  id: 'audit.confirmedBySchedule.scheduleDatetime',
  defaultMessage: 'Schedule',
});

type MetaField = {
  label: MessageDescriptor;
  value: string;
  /** When set, render a schedules-list filter button next to the value. */
  filterCategory?: SchedulesListCategory;
};

type Props = {
  schedule: AuditSchedule;
  /** Match vs schedule difference in minutes. */
  diffMinutes: number | null;
  isOffTolerance: boolean;
  /** Spot or mentions schedules list to open when filtering by order. */
  schedulesListCategory: SchedulesListCategory;
};

/**
 * Builds non-empty schedule metadata rows for the confirmation popover.
 */
function getScheduleMetaFields(
  schedule: AuditSchedule,
  schedulesListCategory: SchedulesListCategory,
): MetaField[] {
  return [
    { label: advertiserFieldMessage, value: schedule.advertiser?.trim() ?? '' },
    { label: campaignFieldMessage, value: schedule.campaign?.trim() ?? '' },
    { label: versionFieldMessage, value: schedule.version?.trim() ?? '' },
    {
      label: orderFieldMessage,
      value: schedule.order?.trim() ?? '',
      filterCategory: schedulesListCategory,
    },
  ].filter((field) => field.value !== '');
}

/**
 * Popover body for schedule confirmation: datetime/tolerance/diff, then schedule metadata.
 */
export function ConfirmedByScheduleDetails({
  schedule,
  diffMinutes,
  isOffTolerance,
  schedulesListCategory,
}: Props) {
  const { $t } = useIntl();
  const metaFields = getScheduleMetaFields(schedule, schedulesListCategory);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="body2">
          <FormattedMessage {...scheduleDatetimeLabelMessage} />
          {': '}
          <LDateTime value={schedule.datetime} showSeconds />
        </Typography>

        <Typography
          variant="body2"
          component="div"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}
        >
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            <FormattedMessage {...toleranceLabelMessage} />
            <Tooltip title={$t(toleranceHelpMessage)}>
              <Box component="span" sx={{ display: 'inline-flex', color: 'text.secondary' }}>
                <HelpOutlineIcon fontSize="small" />
              </Box>
            </Tooltip>
          </Box>
          {': '}
          <ScheduleTolerance config={schedule.toleranceConfig} />
        </Typography>

        {diffMinutes != null && (
          <Typography variant="body2">
            <FormattedMessage {...diffLabelMessage} />
            {`: ${diffMinutes.toFixed(1)} min`}
          </Typography>
        )}

        {isOffTolerance && <ScheduleOffToleranceWarning />}
      </Box>

      {metaFields.length > 0 && (
        <>
          <Divider />
          <Stack direction="column" spacing={0.5}>
            {metaFields.map((field) => (
              <Box key={field.label.id}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {$t(field.label)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="caption">{field.value}</Typography>
                  {field.filterCategory != null && (
                    <ConfirmedByScheduleOrderFilterButton
                      order={field.value}
                      scheduleDatetime={schedule.datetime}
                      category={field.filterCategory}
                    />
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}
